/**
 * Pure client-side helpers for trader-limit math.
 *
 * The relay is the authority: it computes headroom (GET /api/trader/headroom)
 * and is the only gate that can actually block an order. These helpers exist
 * so the trader UI can derive the same answers locally for live previews and
 * the soft "this order would breach" warning, without round-tripping every
 * keystroke.
 */

export type LimitRejectionCode =
  | 'LIMIT_MAX_CONTRACTS'
  | 'LIMIT_MAX_POSITIONS'
  | 'LIMIT_SPORT_NOT_ALLOWED'
  | 'LIMIT_NON_SPORT_DISABLED'
  | 'LIMIT_POLYMARKET_DISABLED'
  | 'LIMIT_POLYMARKET_US_DISABLED'
  | 'LIMIT_MARKET_SPORT_UNKNOWN';

const LIMIT_REJECTION_CODES: readonly LimitRejectionCode[] = [
  'LIMIT_MAX_CONTRACTS',
  'LIMIT_MAX_POSITIONS',
  'LIMIT_SPORT_NOT_ALLOWED',
  'LIMIT_NON_SPORT_DISABLED',
  'LIMIT_POLYMARKET_DISABLED',
  'LIMIT_POLYMARKET_US_DISABLED',
  'LIMIT_MARKET_SPORT_UNKNOWN',
];

/** Render a structured rejection into trader-facing copy. The relay's
 *  `error` field already carries a serviceable string, but for the rule
 *  codes we render a friendlier short form that maps cleanly to UI
 *  affordances (red banner above submit, toast, etc.). LIMIT_MARKET_SPORT_
 *  UNKNOWN gets the transient-friendly "verifying / try again" copy
 *  rather than reading as a hard rejection. */
export function describeLimitRejection(
  code: LimitRejectionCode,
  fallback: string,
): string {
  switch (code) {
    case 'LIMIT_MAX_CONTRACTS':
    case 'LIMIT_MAX_POSITIONS':
      return fallback;
    case 'LIMIT_SPORT_NOT_ALLOWED':
      return 'This sport is not in your account’s allowed list.';
    case 'LIMIT_NON_SPORT_DISABLED':
      return 'Non-sport markets are not enabled for your account.';
    case 'LIMIT_POLYMARKET_DISABLED':
      return 'New Polymarket buys are not enabled for your account. Sells and cancels still work.';
    case 'LIMIT_POLYMARKET_US_DISABLED':
      return 'New Polymarket US buys are not enabled for your account. Sells and cancels still work.';
    case 'LIMIT_MARKET_SPORT_UNKNOWN':
      return 'Verifying new market metadata… try again in a moment if this is a freshly listed market.';
    default:
      return fallback;
  }
}

export interface UserLimits {
  maxContracts: number | null;
  maxPositions: number | null;
}

export interface CurrentExposure {
  /** Σ netCount across all open (ticker, side) on unsettled markets. */
  openContracts: number;
  /** Count of open (ticker, side) keys. */
  openPositions: number;
  /** Resting buy-order intent reported by GET /trader/headroom. */
  intentContracts: number;
  /** Whether this (ticker, side) is already in the trader's open positions.
   *  Used by previewOrder to decide if a new buy adds a position slot. */
  openPositionKeys: ReadonlySet<string>;
}

export interface OrderPreview {
  action: 'buy' | 'sell';
  ticker: string;
  side: 'yes' | 'no';
  count: number;
}

export interface HeadroomView {
  /** Cap or null if unlimited. */
  cap: number | null;
  /** What the trader is currently using toward this cap. */
  current: number;
  /** Remaining headroom (cap - current). null when unlimited. Floored at 0. */
  available: number | null;
  /** current / cap as a fraction in [0, ∞). null when unlimited. */
  fractionUsed: number | null;
  /** Convenience colour bucket for fill bars. */
  status: 'unlimited' | 'green' | 'yellow' | 'red' | 'over';
}

export interface PreviewResult {
  /** What openContracts would be after this order (clamped at 0). */
  nextContracts: number;
  /** What openPositions would be after this order. */
  nextPositions: number;
  /** Set if the order would breach a cap. */
  breach: {
    rule: LimitRejectionCode;
    cap: number;
    current: number;
    requested: number;
  } | null;
}

export function positionKey(ticker: string, side: 'yes' | 'no'): string {
  return `${ticker}|${side}`;
}

/** "Unlimited" for null, "12,000" for 12000. */
export function formatLimitDisplay(value: number | null | undefined): string {
  if (value == null) return 'Unlimited';
  return value.toLocaleString('en-US');
}

/** "12 / 20" or "12 / Unlimited". */
export function formatHeadroomDisplay(current: number, cap: number | null): string {
  return `${current.toLocaleString('en-US')} / ${formatLimitDisplay(cap)}`;
}

/**
 * Compute one cap's headroom view. `current` should already include resting
 * intent for the contracts cap (the relay's /headroom does this); pass the
 * raw count for the positions cap.
 */
export function computeHeadroom(current: number, cap: number | null): HeadroomView {
  if (cap == null) {
    return {
      cap: null,
      current,
      available: null,
      fractionUsed: null,
      status: 'unlimited',
    };
  }
  const available = Math.max(0, cap - current);
  const fractionUsed = cap === 0 ? Infinity : current / cap;
  let status: HeadroomView['status'];
  if (fractionUsed > 1) status = 'over';
  else if (fractionUsed > 0.9) status = 'red';
  else if (fractionUsed > 0.7) status = 'yellow';
  else status = 'green';
  return { cap, current, available, fractionUsed, status };
}

/**
 * Compute what the trader's exposure would look like after submitting one
 * order. Mirrors the relay's checkOrderLimit logic so client-side previews
 * stay in lockstep — sells never grow exposure; buys add to both contracts
 * and (if the key isn't already open) the position count.
 *
 * Position-key novelty is only evaluated when the (ticker, side) key is
 * meaningful. For Polymarket — where tokenId vs conditionId means we can't
 * reliably check key novelty in the UI either — callers should treat the
 * position-cap part of the result as advisory.
 */
export function previewOrder(
  limits: UserLimits,
  exposure: CurrentExposure,
  order: OrderPreview,
): PreviewResult {
  // Sells never grow exposure. Best-effort decrement only — we don't know
  // how much of the named (ticker, side) the trader actually holds without
  // more state, so we cap the reduction at the current openContracts to
  // avoid producing negative numbers in the preview.
  if (order.action === 'sell') {
    const nextContracts = Math.max(0, exposure.openContracts - order.count);
    return {
      nextContracts,
      nextPositions: exposure.openPositions,
      breach: null,
    };
  }

  const totalCommitted = exposure.openContracts + exposure.intentContracts;
  const nextContracts = exposure.openContracts + order.count;
  const isExistingKey = exposure.openPositionKeys.has(positionKey(order.ticker, order.side));
  const nextPositions = isExistingKey ? exposure.openPositions : exposure.openPositions + 1;

  if (limits.maxContracts != null && totalCommitted + order.count > limits.maxContracts) {
    return {
      nextContracts,
      nextPositions,
      breach: {
        rule: 'LIMIT_MAX_CONTRACTS',
        cap: limits.maxContracts,
        current: totalCommitted,
        requested: order.count,
      },
    };
  }

  if (
    limits.maxPositions != null &&
    !isExistingKey &&
    exposure.openPositions + 1 > limits.maxPositions
  ) {
    return {
      nextContracts,
      nextPositions,
      breach: {
        rule: 'LIMIT_MAX_POSITIONS',
        cap: limits.maxPositions,
        current: exposure.openPositions,
        requested: 1,
      },
    };
  }

  return { nextContracts, nextPositions, breach: null };
}

/**
 * Convenience: does the trader have any active cap (either column non-null)?
 * Used by the LimitsWidget to decide whether to render itself at all.
 */
export function hasAnyLimit(limits: UserLimits): boolean {
  return limits.maxContracts != null || limits.maxPositions != null;
}

// ── Structured rejection error ────────────────────────────────────────────

export interface LimitRejectionBreach {
  rule: LimitRejectionCode;
  cap: number;
  current: number;
  requested: number;
}

/**
 * Thrown by the order-placement actions when the relay returns a 403 with
 * a LIMIT_* code. Carries the structured breach so callers (order forms,
 * defensive guards) can render specific UI rather than a generic toast.
 */
export class LimitRejectionError extends Error {
  readonly code: LimitRejectionCode;
  readonly breach: LimitRejectionBreach;
  constructor(message: string, code: LimitRejectionCode, breach: LimitRejectionBreach) {
    super(message);
    this.name = 'LimitRejectionError';
    this.code = code;
    this.breach = breach;
  }
}

/** True when the body looks like our structured limit-rejection envelope. */
export function isLimitRejectionPayload(body: unknown): body is {
  error: string;
  code: LimitRejectionCode;
  breach: LimitRejectionBreach;
} {
  if (!body || typeof body !== 'object') return false;
  const b = body as Record<string, unknown>;
  return (
    LIMIT_REJECTION_CODES.includes(b.code as LimitRejectionCode) &&
    typeof b.error === 'string' &&
    typeof b.breach === 'object' &&
    b.breach !== null
  );
}
